'use client';
import { KeyboardShortcutRegistryContext } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/internal/registryState.js';
import { parseKeybindings } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/keybinding.js';
import { getShortcutDefinition, resolvePlatformKeybinding, } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/shortcutManifest.js';
import { useContext, useEffect, useRef } from 'react';
/**
 * Sentinel value a shortcut handler can return to signal that it did not
 * consume the event. The dispatcher will then continue down the stack to
 * the next matching handler. When the handler returns anything else
 * (including `undefined` / `void`), the event is considered consumed and
 * dispatch stops — preserving the original "first match wins" contract.
 */
export const SHORTCUT_YIELD = Symbol.for('SHORTCUT_YIELD');
/**
 * Converts a `ShortcutRegistration`'s keybinding array into a concrete
 * `KeyboardEvent` matcher.
 */
export function resolveMatch(reg) {
    return parseKeybindings(...reg.keybinding);
}
/**
 * Returns the registry context from the nearest `KeyboardShortcutsProvider`.
 *
 * @throws When called outside `KeyboardShortcutsProvider`. Hooks that register
 * or display shortcuts inherit this provider requirement.
 */
export function useShortcutRegistry() {
    const ctx = useContext(KeyboardShortcutRegistryContext);
    if (!ctx) {
        throw new Error('useShortcutRegistry must be used within a KeyboardShortcutsProvider');
    }
    return ctx;
}
/**
 * Registers a manifest-backed shortcut for the lifetime of the calling component.
 * Must be called under `KeyboardShortcutsProvider`; otherwise the registry lookup
 * throws. The provider disables registrations on mobile, so mobile shortcuts are
 * transparent to dispatch and hidden from display helpers.
 *
 * Shortcut handlers are responsible for calling `event.preventDefault()` when
 * they need to suppress browser defaults. Returning `void` consumes the match;
 * return {@link SHORTCUT_YIELD} to let lower-priority registrations run.
 */
export function useRegisterShortcut(input) {
    const { register, isMacOS, isMobileDevice } = useShortcutRegistry();
    const { id, handler, isEnabled, isAvailable } = input;
    const handlerRef = useRef(handler);
    handlerRef.current = handler;
    useEffect(() => {
        const def = getShortcutDefinition(id);
        return register({
            id,
            keybinding: resolvePlatformKeybinding(def.defaultKeybinding, isMacOS),
            handler: (event) => handlerRef.current(event),
            isEnabled: !isMobileDevice && isEnabled !== false,
            isAvailable,
            label: def.label,
            group: def.group,
        });
    }, [register, id, isEnabled, isAvailable, isMacOS, isMobileDevice]);
}
//# sourceMappingURL=registry.js.map